/**
 * Composants transverses : badges, cartes de tâche, feuille modale, sections.
 *
 * La carte de tâche est le composant le plus réutilisé de l'application : liseré
 * de catégorie, titre, échéance en langage naturel, badges d'origine et grande
 * zone de validation atteignable au pouce. Tout le reste s'articule autour.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Backpack,
  Bell,
  BellOff,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Droplets,
  FileText,
  Flame,
  Hammer,
  HeartPulse,
  House,
  Lightbulb,
  Pencil,
  Scale,
  ShoppingBasket,
  Sparkles,
  Stamp,
  Users,
  Wrench,
  X,
} from 'lucide-react';

import {
  type Categorie,
  type Tache,
  LIBELLE_CATEGORIE,
  enClair,
  enCourt,
  enClairAvecJour,
  relatif,
  resumeRrule,
} from '@foyer/noyau';

/** Lucide impose 2.75 dans le système Organic : les traits fins jurent. */
export const TRAIT = 2.75;

/**
 * « jeudi 20 août » plutôt que « jeudi 20 · jeudi 20 août ».
 *
 * Dans la semaine, `relatif()` rend déjà le jour et le numéro : répéter la date
 * complète juste après n'apprend rien. Au-delà, les deux se complètent.
 */
export function echeanceEnClair(echeance: string, aujourdhui: string): string {
  const rel = relatif(echeance, aujourdhui);
  const complet = enClairAvecJour(echeance);
  return complet.startsWith(rel.texte) ? complet : `${rel.texte} · ${complet}`;
}

export const COULEUR_CATEGORIE: Record<string, string> = {
  sante: 'var(--cat-sante)',
  maison: 'var(--cat-maison)',
  chauffage: 'var(--cat-chauffage)',
  assainissement: 'var(--cat-assainissement)',
  ecole: 'var(--cat-ecole)',
  travaux: 'var(--cat-travaux)',
  administratif: 'var(--cat-administratif)',
  courses: 'var(--cat-courses)',
};

/**
 * Une icône par catégorie.
 *
 * `PastilleCategorie` affichait `FileText` pour tout le monde : santé, courses,
 * chauffage, école. Huit catégories, une seule icône — l'œil n'avait donc que le
 * libellé pour se repérer, et l'ensemble paraissait terne alors que le système de
 * couleurs était là. Chaque catégorie porte maintenant sa propre figure.
 */
export const ICONE_CATEGORIE: Record<string, typeof FileText> = {
  sante: HeartPulse,
  maison: House,
  chauffage: Flame,
  assainissement: Droplets,
  ecole: Backpack,
  travaux: Hammer,
  administratif: Stamp,
  courses: ShoppingBasket,
};

// ── Badges ──────────────────────────────────────────────────────────────────

/**
 * Règle produit : **toute échéance affiche son origine**, et une obligation
 * légale cite son texte de référence. Le libellé propre à la tâche prime quand il
 * existe — « garantie légale » plutôt qu'« obligation légale », qui laisserait
 * croire à un devoir.
 */
export function BadgeOrigine({
  origine,
  libelle,
}: {
  origine: string | null;
  libelle?: string | null;
}) {
  if (!origine && !libelle) return null;
  const table: Record<string, { texte: string; classe: string; Icone: typeof Scale }> = {
    legale: { texte: 'obligation légale', classe: 'legale', Icone: Scale },
    obligatoire: { texte: 'obligatoire', classe: 'legale', Icone: Scale },
    constructeur: { texte: 'constructeur', classe: 'constructeur', Icone: Wrench },
    bonne_pratique: { texte: 'bonne pratique', classe: 'pratique', Icone: Lightbulb },
    recommande_assurance_maladie: { texte: 'Assurance maladie', classe: 'pratique', Icone: Sparkles },
    manuel: { texte: 'ajouté à la main', classe: 'manuel', Icone: Pencil },
  };
  const b = table[origine ?? ''] ?? { texte: origine ?? '', classe: 'manuel', Icone: Pencil };
  return (
    <span className={`badge ${b.classe}`}>
      <b.Icone strokeWidth={TRAIT} aria-hidden />
      {libelle ? libelle.replace(/^[^\p{L}]+/u, '') : b.texte}
    </span>
  );
}

export function BadgesTache({ tache }: { tache: Tache }) {
  return (
    <span className="badges">
      <BadgeOrigine origine={tache.origineReglementaire ?? tache.origine} libelle={tache.libelleOrigine} />
      {tache.reference && <span className="badge reference">{tache.reference}</span>}
      {tache.rrule && <span className="badge">↻ {resumeRrule(tache.rrule)}</span>}
      {tache.ancrageVacances && <span className="badge">↻ vacances scolaires</span>}
      {tache.modeleCle && !tache.interventionUtilisateur && (
        <span className="badge deduit">
          <Sparkles strokeWidth={TRAIT} aria-hidden />
          déduit
        </span>
      )}
      {tache.echeanceEstimee && <span className="badge attention">estimée, à ajuster</span>}
      {tache.echeanceAConfirmer && <span className="badge attention">date à confirmer</span>}
      {tache.consommable && (
        <span className="badge">
          <ShoppingBasket strokeWidth={TRAIT} aria-hidden />
          {tache.consommable}
        </span>
      )}
    </span>
  );
}

// ── Carte de tâche ──────────────────────────────────────────────────────────

export interface ProprietesCarteTache {
  tache: Tache;
  aujourdhui: string;
  /** Prénom de l'assigné, quand ce n'est pas l'utilisateur courant. */
  assigneAutre?: string | null;
  /**
   * Membres du foyer, pour assigner sans quitter la liste. Un seul membre : le
   * sélecteur ne s'affiche pas — s'assigner une tâche à soi-même dans un foyer
   * d'une personne ne décide de rien.
   */
  membres?: { id: string; prenom: string }[];
  /** Utilisateur courant, pour dire « moi » au lieu de répéter son prénom. */
  moiId?: string | null;
  /** Assigner, ou rendre à tout le monde avec `null`. */
  onAssigne?(id: string | null): void;
  onCoche?(): void;
  onOuvre?(): void;
  occupe?: boolean;
  /**
   * Report. Optionnel : l'agenda et les frises affichent des tâches qu'on ne
   * reporte pas depuis là, et un bouton inutile encombre.
   */
  onReporte?(jours: number): void;
  onAnnuleReport?(): void;
  /**
   * Sourdine de relance. N'apparaît que sur une tâche **en retard** : c'est le
   * seul moment où la relance existe, et proposer de couper un rappel qui n'a
   * pas encore eu lieu n'aurait aucun sens.
   */
  onSourdine?(muette: boolean): void;
}

export function CarteTache({
  tache,
  aujourdhui,
  assigneAutre,
  membres = [],
  moiId,
  onAssigne,
  onCoche,
  onOuvre,
  occupe,
  onReporte,
  onAnnuleReport,
  onSourdine,
}: ProprietesCarteTache) {
  /*
   * Repliée par défaut : sur téléphone, une carte dépliée en laissait tenir
   * trois par écran. Repliée, on ne garde que le titre et la récurrence.
   *
   * L'échéance fait exception **quand la tâche est en retard** : le contour
   * corail ne doit jamais porter seul l'information, et « En retard de 7 jours »
   * est le libellé qui le double. C'est la règle 1 du système, pas un oubli.
   */
  const [deplie, setDeplie] = useState(false);

  const rel = tache.echeance ? relatif(tache.echeance, aujourdhui) : null;
  const enRetard = rel?.urgence === 'retard';
  const recurrence = tache.rrule
    ? resumeRrule(tache.rrule)
    : tache.ancrageVacances
      ? 'vacances scolaires'
      : null;
  const faite = tache.statut === 'terminee';

  const classes = ['tache'];
  if (deplie) classes.push('deplie');
  if (enRetard && !faite) classes.push('retard');
  if (faite) classes.push('terminee');
  // Reportée : présente mais en retrait. La masquer complètement ferait croire à
  // une disparition, et on ne saurait plus comment revenir en arrière.
  if (tache.reporteA && tache.reporteA > aujourdhui) classes.push('reportee');

  return (
    <article className={classes.join(' ')} style={{ ['--liseré' as string]: COULEUR_CATEGORIE[tache.categorie] }}>
      <div className="corps">
        <button
          type="button"
          className="titre"
          onClick={onOuvre}
          style={{
            background: 'none',
            border: 0,
            padding: 0,
            textAlign: 'left',
            font: 'inherit',
            fontWeight: 600,
            color: 'inherit',
            cursor: onOuvre ? 'pointer' : 'default',
            width: '100%',
          }}
        >
          {tache.titre}
        </button>
        {deplie && (
          <div className="meta">
            {rel ? echeanceEnClair(tache.echeance!, aujourdhui) : 'Sans date'}
            {assigneAutre && ` · pour ${assigneAutre}`}
          </div>
        )}
        {deplie ? (
          <BadgesTache tache={tache} />
        ) : (
          /*
           * Repliée, la rangée tient sur deux lignes : le titre, puis la date.
           *
           * La date était absente et le retard tenait dans une pastille. Dans un
           * almanach, la date est ce qu'on lit en premier — c'est la question
           * posée à l'application. Elle redevient donc une ligne de texte,
           * composée dans la serif, et le retard s'y rattache après un point
           * médian au lieu d'occuper une pastille à lui seul.
           *
           * Le libellé reste écrit en toutes lettres : la couleur rouge ne
           * porte jamais seule l'information.
           */
          /*
           * Pas de date et rien à répéter : pas de ligne du tout. « sans date »
           * sous chaque titre de l'écran « Quand j'ai le temps » répétait au
           * lecteur ce que le titre de l'écran venait de lui dire.
           */
          !tache.echeance && !recurrence ? null : (
          <div className="ligne-date">
            {tache.echeance ? enCourt(tache.echeance) : 'sans date'}
            {/*
              « 28 j de retard » plutôt que « en retard de 28 jours » : la ligne
              est coupée à la largeur de la colonne, et c'est le nombre qui
              porte l'information — il ne doit pas être ce qu'on tronque.
            */}
            {enRetard &&
              !faite &&
              ` · ${rel!.jours === -1 ? 'depuis hier' : `${-rel!.jours} j de retard`}`}
            {recurrence && tache.echeance && ` · ${recurrence}`}
            {recurrence && !tache.echeance && recurrence}
          </div>
          )
        )}
      </div>

      {assigneAutre && <span className="avatar">{assigneAutre.slice(0, 1).toUpperCase()}</span>}

      <button
        type="button"
        className="chevron-tache"
        onClick={() => setDeplie((v) => !v)}
        aria-expanded={deplie}
        aria-label={deplie ? `Replier « ${tache.titre} »` : `Détails de « ${tache.titre} »`}
        title={deplie ? 'Replier' : 'Détails'}
      >
        <ChevronDown strokeWidth={TRAIT} size={20} aria-hidden />
      </button>

      {onCoche && (
        <button
          type="button"
          className={`cocher${faite ? ' faite' : ''}${assigneAutre ? ' autre' : ''}`}
          onClick={onCoche}
          disabled={occupe}
          aria-label={faite ? `Annuler « ${tache.titre} »` : `Marquer « ${tache.titre} » comme fait`}
          title={faite ? 'Annuler' : 'Marquer comme fait'}
        >
          <Check strokeWidth={TRAIT} size={22} aria-hidden />
        </button>
      )}
      <div className="pied-tache" hidden={!deplie}>
        {onAssigne && membres.length > 1 && (
          /*
           * Qui s'en occupe, réglé depuis la liste. Le trajet d'avant —
           * ouvrir la tâche, déplier « Plus d'options… », choisir dans un
           * menu, enregistrer — coûtait quatre gestes à une décision qui se
           * prend en une seconde : « celle-là, c'est pour toi ».
           *
           * Des pastilles plutôt qu'un menu : à deux ou trois membres, le
           * choix entier tient sous le pouce, et l'état courant se lit sans
           * l'ouvrir. Un menu fermé, lui, ne montre rien.
           */
          <div className="assignation" role="group" aria-label={`Assigner « ${tache.titre} »`}>
            <button
              type="button"
              className={`pastille-membre${tache.assigneA ? '' : ' actif'}`}
              onClick={() => onAssigne(null)}
              disabled={occupe}
              aria-pressed={!tache.assigneA}
              aria-label="Pour tout le monde"
              title="Pour tout le monde"
            >
              <Users strokeWidth={TRAIT} size={17} aria-hidden />
            </button>
            {membres.map((m) => {
              const retenu = tache.assigneA === m.id;
              const pour = m.id === moiId ? 'Pour moi' : `Pour ${m.prenom}`;
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`pastille-membre${retenu ? ' actif' : ''}`}
                  // Retoucher la pastille déjà retenue rend la tâche à tout le
                  // monde : sans ça, il n'y a aucun chemin de retour depuis la
                  // liste, et il faudrait rouvrir le formulaire pour désassigner.
                  onClick={() => onAssigne(retenu ? null : m.id)}
                  disabled={occupe}
                  aria-pressed={retenu}
                  aria-label={pour}
                  title={pour}
                >
                  {m.prenom.slice(0, 1).toUpperCase()}
                </button>
              );
            })}
          </div>
        )}
        {onReporte && (
          <BoutonReporter
            titre={tache.titre}
            reporteA={tache.reporteA}
            aujourdhui={aujourdhui}
            occupe={occupe}
            onReporte={onReporte}
            onAnnule={() => onAnnuleReport?.()}
          />
        )}
        {onSourdine && enRetard && !faite && (
          <button
            type="button"
            className={tache.relanceMuette ? 'bouton texte muette' : 'bouton texte'}
            onClick={() => onSourdine(!tache.relanceMuette)}
            disabled={occupe}
            aria-pressed={tache.relanceMuette}
            aria-label={
              tache.relanceMuette
                ? `Réactiver les rappels de « ${tache.titre} »`
                : `Ne plus me rappeler « ${tache.titre} »`
            }
            title={
              tache.relanceMuette
                ? 'Les relances sont coupées pour cette tâche'
                : 'Ne plus me relancer sur cette tâche'
            }
          >
            {tache.relanceMuette ? (
              <BellOff strokeWidth={TRAIT} size={15} aria-hidden />
            ) : (
              <Bell strokeWidth={TRAIT} size={15} aria-hidden />
            )}
            {tache.relanceMuette ? 'En sourdine' : 'Ne plus me rappeler'}
          </button>
        )}
      </div>
    </article>
  );
}


// ── Report ──────────────────────────────────────────────────────────────────

/**
 * Raccourcis de report.
 *
 * Trois choix couvrent l'écrasante majorité des cas — « pas aujourd'hui »,
 * « la semaine prochaine », « le mois prochain » — et le quatrième rend la main.
 * Une liste plus longue ferait réfléchir là où l'on veut justement ne pas
 * réfléchir : on reporte pour se débarrasser d'une ligne, pas pour planifier.
 */
export const REPORTS = [
  { jours: 1, libelle: 'Demain' },
  { jours: 7, libelle: 'Dans une semaine' },
  { jours: 30, libelle: 'Dans un mois' },
] as const;

/**
 * Bouton « Reporter » et son petit menu.
 *
 * Le menu s'ouvre au clic et se referme au choix ou à l'échappement. Pas de
 * fermeture au clic extérieur : sur mobile le geste n'existe pas vraiment, et
 * un menu qui se ferme tout seul pendant qu'on vise est plus agaçant qu'utile.
 */
export function BoutonReporter({
  titre,
  reporteA,
  aujourdhui,
  occupe,
  onReporte,
  onAnnule,
}: {
  titre: string;
  reporteA: string | null;
  aujourdhui: string;
  occupe?: boolean;
  onReporte(jours: number): void;
  onAnnule(): void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const estReporte = Boolean(reporteA && reporteA > aujourdhui);

  return (
    <>
      <button
        type="button"
        className={estReporte ? 'bouton texte reporte' : 'bouton texte'}
        onClick={() => setOuvert(true)}
        disabled={occupe}
        aria-label={estReporte ? `Reportée jusqu'au ${enClairAvecJour(reporteA!)}` : 'Reporter'}
      >
        <Clock strokeWidth={TRAIT} size={15} aria-hidden />
        {estReporte ? `Jusqu'au ${enClair(reporteA!)}` : 'Reporter'}
      </button>

      {ouvert && (
        <Feuille titre={`Reporter « ${titre} »`} onFerme={() => setOuvert(false)}>
          <p className="sourdine">
            L'échéance ne bouge pas : la tâche reste due à sa date, elle cesse simplement
            d'apparaître jusque-là.
          </p>
          <div className="choix-report">
            {REPORTS.map((r) => (
              <button
                key={r.jours}
                type="button"
                className="bouton secondaire"
                onClick={() => {
                  setOuvert(false);
                  onReporte(r.jours);
                }}
              >
                {r.libelle}
              </button>
            ))}
          </div>
          {estReporte && (
            <div className="actions">
              <button
                type="button"
                className="bouton texte"
                onClick={() => {
                  setOuvert(false);
                  onAnnule();
                }}
              >
                Ne plus reporter
              </button>
            </div>
          )}
        </Feuille>
      )}
    </>
  );
}

// ── Section repliable ───────────────────────────────────────────────────────

export function Section({
  titre,
  description,
  compte,
  urgente,
  ouverteParDefaut = true,
  children,
}: {
  titre: string;
  /** Une phrase sous le titre, quand l'intitulé seul ne suffit pas à situer la section. */
  description?: string;
  compte?: number;
  urgente?: boolean;
  ouverteParDefaut?: boolean;
  children: ReactNode;
}) {
  const [ouverte, setOuverte] = useState(ouverteParDefaut);
  if (compte === 0) return null;
  return (
    <section>
      <button
        type="button"
        className={`section-titre${urgente ? ' urgente' : ''}`}
        onClick={() => setOuverte((o) => !o)}
        aria-expanded={ouverte}
      >
        {ouverte ? <ChevronDown size={14} strokeWidth={TRAIT} /> : <ChevronRight size={14} strokeWidth={TRAIT} />}
        {titre}
        {compte !== undefined && <span className="compte">{compte}</span>}
      </button>
      {ouverte && description && <p className="section-description">{description}</p>}
      {ouverte && children}
    </section>
  );
}

// ── Feuille modale ──────────────────────────────────────────────────────────

export function Feuille({
  titre,
  onFerme,
  children,
  pied,
}: {
  titre: string;
  onFerme(): void;
  children: ReactNode;
  pied?: ReactNode;
}) {
  // Échap ferme : au clavier comme au doigt, on doit pouvoir sortir.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFerme();
    };
    window.addEventListener('keydown', surTouche);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', surTouche);
      document.body.style.overflow = '';
    };
  }, [onFerme]);

  return (
    <div
      className="voile"
      role="dialog"
      aria-modal="true"
      aria-label={titre}
      onClick={(e) => {
        if (e.target === e.currentTarget) onFerme();
      }}
    >
      <div className="feuille">
        <div className="poignee" />
        <div className="feuille-entete">
          <h2>{titre}</h2>
          <button type="button" className="bouton icone" onClick={onFerme} aria-label="Fermer">
            <X strokeWidth={TRAIT} size={20} aria-hidden />
          </button>
        </div>
        {children}
        {pied && <div className="actions">{pied}</div>}
      </div>
    </div>
  );
}

// ── Petits utilitaires d'affichage ──────────────────────────────────────────

export function Champ({
  etiquette,
  aide,
  children,
}: {
  etiquette: string;
  aide?: string;
  children: ReactNode;
}) {
  return (
    <div className="champ">
      <label className="etiquette">{etiquette}</label>
      {children}
      {aide && <div className="aide">{aide}</div>}
    </div>
  );
}

export function Interrupteur({
  actif,
  onChange,
  etiquette,
}: {
  actif: boolean;
  onChange(v: boolean): void;
  etiquette: string;
}) {
  return (
    <span className="interrupteur">
      <input type="checkbox" checked={actif} onChange={(e) => onChange(e.target.checked)} aria-label={etiquette} />
      <span className="piste" />
      <span className="pastille" />
    </span>
  );
}

export function BadgeCategorie({ categorie }: { categorie: Categorie }) {
  return (
    <span
      className="badge categorie"
      style={{
        ['--cat-fond' as string]: `var(--cat-${categorie}-fond)`,
        ['--cat-encre' as string]: `var(--cat-${categorie}-encre)`,
      }}
    >
      {LIBELLE_CATEGORIE[categorie]}
    </span>
  );
}

export function Vide({
  titre,
  texte,
  illustration,
  children,
}: {
  titre: string;
  texte: string;
  /** Dessin au-dessus du titre. Facultatif : le titre et la phrase suffisent. */
  illustration?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="vide">
      {illustration}
      <div className="grand">{titre}</div>
      <p>{texte}</p>
      {children && <div className="actions" style={{ justifyContent: 'center' }}>{children}</div>}
    </div>
  );
}

export function Chargement({ quoi = 'Chargement' }: { quoi?: string }) {
  return <div className="chargement">{quoi}…</div>;
}

export function Erreur({ message }: { message: string }) {
  return <div className="alerte">{message}</div>;
}
